import type { Envelope, HkEvent, HkTableState, Suit } from "../gen/bazivan/v1/table_pb";
import { client } from "../net/client";
import { signal } from "../net/signal";
import { Pace } from "../net/pace";
import { showCarpet } from "../game/carpet";
import { Sequencer } from "../scene/queue";
import { introGate } from "../game/stake";
import { buildOpeningProof, DealWatch, hkCode, type DealProof } from "../game/dealLock";
import { BreakWatch, RESULT_EVENTS } from "../game/handBreak";
import { botText } from "../game/seats";
import { toLocal } from "../hokm/state";
import { applyHaft, dealSize, emptyHaft, toServer, type HaftLocal } from "./state";
import type { HaftScene } from "./scene";

/**
 * کنترلرِ میزِ آنلاینِ هفت‌خبیث — پورتِ HaftOnlineController.
 *
 * این‌جا هیچ قاعده‌ای اجرا نمی‌شود؛ حقیقت همیشه HkTableStateِ سرور است. سه کار:
 *   • نمای سرور را با applyHaft ترجمه و صحنه را تازه کند،
 *   • رویدادها را **به ترتیب** انیمیت کند (صفِ تک‌نخی، چون سرور اول رویداد
 *     و بعد وضعیت می‌فرستد و انیمیشن باید پیش از نشستنِ وضعیتِ تازه تمام شود)،
 *   • لمسِ بازیکن را به پیامِ HkPlay/HkDraw/… تبدیل کند.
 */
export class HaftOnline {
  private seq = new Sequencer();
  private scene: HaftScene;
  private off: (() => void) | null = null;
  private gotState = false; private seatsLaid = false; private quitting = false; private carpetApplied = false;
  /**
   * دو‌بار‌زدن (net/pace.ts): لمسِ دومِ همان دکمه در این فاصله کنار می‌رود. دسته مهم‌تر از همه است: لمسِ دومش
   * پس از کشیدنِ کارتِ بازی‌شدنی «رد» است و نوبت را می‌داد (سرور هم همین را دارد — table/haft.go hkPassGuard).
   */
  private tap = new Pace(0, 1, 600);
  private suitPickerOpen = false; private targetPickerOpen = false; private offerOpen = false;
  /**
   * خودم کارتِ تعارف را برداشتم و animOfferTake نشانش داد.
   *
   * رویدادِ «offer» بعدش از سرور می‌رسد و بی این نشانه، همان یک کارت دو بار
   * پرواز می‌کرد.
   */
  private tookOffer = false;
  private startShown = false;
  private ceremonyDone = false;
  private st: HaftLocal = emptyHaft();
  private turnEndsAt = 0; private turnTotal = 0; private tick: ReturnType<typeof setInterval> | null = null;

  /** پایانِ دور/مسابقه — پنجره‌ی نتایج را ری‌اکت می‌کشد (ShowRoundOver). */
  readonly overlay = signal(false);
  /** مکثِ میانِ دو دست — «ادامه» ِ همه ([[BreakWatch]]). */
  readonly brk = new BreakWatch();
  /** قفلِ پخشِ دستی که تمام شد — [[DealProof]]؛ null = نشانی نیست. */
  readonly proof = signal<DealProof | null>(null);
  private deal = new DealWatch();
  /** آخرین وضعیتِ خام، برای جدولِ نتایج. */
  readonly raw = signal<HkTableState | null>(null);

  constructor(scene: HaftScene) { this.scene = scene; }
  get state(): HaftLocal { return this.st; }

  start(): void {
    this.off = client.on((env) => this.onMessage(env));
    // پرده‌ی شرط بالاست؟ صحنه پیام‌ها را از همین حالا می‌گیرد، ولی تا
    // بالا رفتنِ پرده چیزی نمی‌کشد — وگرنه آس‌کشی و پخشِ ورق پشتِ پرده
    // انجام می‌شد و بازیکن میزی را می‌دید که ورق‌ها در دستش سبز شده‌اند.
    this.seq.hold(introGate());
    // پخشِ دستِ تازه پیش از سوارشدنِ این صفحه رسیده؛ از سیگنال برش می‌داریم.
    const ace = client.hkAce.value;
    if (ace) this.seq.enqueue(() => this.playEvent(ace));
    const deal = client.hkDeal.value;
    if (deal) this.seq.enqueue(() => this.playEvent(deal));
    const last = client.hkTable.value;
    if (last) this.seq.enqueueSnapshot(() => this.applyState(last));
    this.tick = setInterval(() => this.updateTimer(), 200);
    (window as unknown as { __haft?: HaftOnline }).__haft = this;
  }

  dispose(): void {
    this.quitting = true;
    this.off?.(); this.off = null;
    this.seq.dispose();
    if (this.tick) clearInterval(this.tick);
  }

  // ---------------------------------------------------------------- ورودیِ بازیکن

  clickCard(i: number): void {
    if (this.quitting || !this.gotState) return;
    const hand = this.st.myHand;
    if (i < 0 || i >= hand.length) return;
    // سرور خودش می‌گوید کدام کارت بازی‌شدنی است (my_playable) — قاعده‌ی هفت‌خبیث حالت‌دار است
    if (this.st.playable.length > i && !this.st.playable[i]) { this.scene.toast("این کارت را نمی‌شود بازی کرد"); return; }
    client.hkPlay(hand[i]!);
  }

  draw(): void { if (!this.quitting && this.gotState && this.tap.take("draw")) client.hkDraw(); }

  pickSuit(s: Suit): void {
    if (this.quitting) return;
    if (this.suitPickerOpen) { this.scene.closeSuitPicker(); this.suitPickerOpen = false; }
    client.hkPickSuit(s);
  }

  /** لمسِ صندلی: هدفِ کارتِ ۲، یا مچ‌گیریِ تک‌کارتِ فراموش‌شده. */
  seatClick(p: number): void {
    if (this.quitting || p <= 0) return;
    if (this.st.awaitingTarget) {
      if (this.targetPickerOpen) { this.scene.closeTargetPicker(); this.targetPickerOpen = false; }
      client.hkPickTarget(toServer(p, this.st.me, this.st.n));
      return;
    }
    if (this.st.catchOpen && this.st.catchSeat === p && this.tap.take("catch")) client.hkCatch();
  }

  pickOffered(i: number): void {
    if (this.quitting || !this.st.awaitingOffer) return;
    if (this.offerOpen) { void this.scene.animOfferTake(i); this.offerOpen = false; this.tookOffer = true; }
    client.hkPickOffer(i);
  }

  callUno(): void { if (!this.quitting && this.tap.take("uno")) client.hkCallWarn(); }
  catchUno(): void { if (!this.quitting && this.st.catchOpen && this.tap.take("catch")) client.hkCatch(); }
  /** «ادامه» — [[HokmOnline.nextHand]]. جدولِ نتایج با وضعیتِ دورِ تازه خودش بسته می‌شود. */
  nextRound(): void { if (!this.brk.press()) this.overlay.value = false; }

  // ---------------------------------------------------------------- پیام‌های سرور

  private onMessage(env: Envelope): void {
    if (this.quitting) return;
    switch (env.msg.case) {
      case "hkState": { const ts = env.msg.value; this.seq.enqueueSnapshot(() => this.applyState(ts), this.brk.mustShow(ts.brk)); break; }
      case "hkEvent": { const ev = env.msg.value; this.seq.enqueue(() => this.playEvent(ev), RESULT_EVENTS.has(ev.kind)); break; }
      case "error": this.showError(env.msg.value.code); break;
    }
  }

  private showError(code: string): void {
    const msg: Record<string, string> = { not_your_turn: "نوبت تو نیست", illegal_card: "این کارت را نمی‌شود بازی کرد", not_in_hand: "این کارت در دستت نیست" };
    if (msg[code]) this.scene.toast(msg[code]!);
  }

  private async applyState(ts: HkTableState): Promise<void> {
    // پنلِ نتیجه دستِ کم PANEL_MIN_MS دیده شود، حتی اگر این صفحه پشتِ سرور مانده — [[BreakWatch.keep]].
    await this.brk.keep(this.seq, ts.brk, !!this.overlay.value && !this.st.matchOver);
    if (this.quitting) return;
    this.st = applyHaft(ts);
    this.raw.value = ts;
    this.brk.update(ts.brk, ts.seats, ts.me, ts.watching);
    // فقط پخشِ آغاز قفل است؛ دستِ آغاز را فقط اگر لحظه‌ی پخش را دیدیم نگه می‌داریم.
    if (!ts.watching && !ts.roundOver) {
      const fresh = ts.handCounts.length > 0 && ts.handCounts.every((n) => n === ts.myHand.length);
      this.deal.observe(ts.dealLock, ts.myHand.map(hkCode), fresh);
    }
    this.gotState = true;
    this.scene.setInputLocked(!this.st.myTurn);

    if (!this.carpetApplied && this.st.carpet) { showCarpet(this.st.carpet); this.carpetApplied = true; }
    if (!this.seatsLaid) { this.scene.layoutSeats(this.st); this.seatsLaid = true; }

    this.scene.render(this.st);
    this.scene.renderPiles(this.st);
    this.scene.updateCounts(this.st);

    // سؤال‌های باز: هر کدام فقط وقتی که **سرور** گفته از من می‌پرسد.
    this.suitPickerOpen = this.setPicker(this.suitPickerOpen, this.st.awaitingSuit, () => this.scene.openSuitPicker(), () => this.scene.closeSuitPicker());
    this.targetPickerOpen = this.setPicker(this.targetPickerOpen, this.st.awaitingTarget, () => this.scene.openTargetPicker(), () => this.scene.closeTargetPicker());
    if (this.st.awaitingOffer && !this.offerOpen && this.st.offerFrom >= 0) { this.scene.openOffer(this.st, this.st.offerFrom); this.offerOpen = true; }
    else if (!this.st.awaitingOffer && this.offerOpen) { this.scene.closeOffer(); this.offerOpen = false; }

    this.scene.showUnoButton(this.st.warnOpen, this.st.warnLeft);
    this.scene.showCatchButton(this.st.catchOpen, this.st.catchLeft);

    if (this.st.roundOver && !this.overlay.value) {
      const n = ts.seats.length;
      this.proof.value = ts.watching ? null : await buildOpeningProof(this.deal, ts.dealSecret, ts.dealPiles, n, ts.me,
        (s) => toLocal(s, ts.me, n), "ورق‌ها پیش از بازی مهر شد و کسی به آن دست نزد. (کشیدن‌های بعد از بُر دوباره جزوش نیست.)");
      this.overlay.value = true;
    }
    else if (!this.st.roundOver && this.overlay.value) this.overlay.value = false;

    const asking = this.st.myTurn || this.st.awaitingSuit || this.st.awaitingTarget || this.st.awaitingOffer;
    if (asking && this.st.turnRemaining > 0.1) {
      this.turnTotal = Math.max(this.turnTotal, this.st.turnRemaining);
      this.turnEndsAt = performance.now() + this.st.turnRemaining * 1000;
    } else { this.turnEndsAt = 0; this.turnTotal = 0; this.scene.setTimer(0, 0); }
  }

  private setPicker(open: boolean, want: boolean, show: () => void, hide: () => void): boolean {
    if (want && !open) { show(); return true; }
    if (!want && open) { hide(); return false; }
    return open;
  }

  private updateTimer(): void {
    if (this.turnEndsAt <= 0) return;
    const left = (this.turnEndsAt - performance.now()) / 1000;
    if (left <= 0) { this.turnEndsAt = 0; this.scene.setTimer(0, 0); return; }
    this.scene.setTimer(left, Math.max(left, this.turnTotal));
  }

  private async playEvent(ev: HkEvent): Promise<void> {
    const n = this.st.n;
    const seat = toLocalSafe(ev.seat, this.st.me, n);
    const victim = toLocalSafe(ev.victim, this.st.me, n);
    switch (ev.kind) {
      case "ace_draw": {
        // مراسم پیش از هر چیز: صحنه باید صندلی‌ها را داشته باشد، پس اگر
        // وضعیت هنوز نرسیده با آخرین نمای سرور می‌سازیمش — [[HokmOnline]].
        if (this.ceremonyDone || ev.draw.length === 0) return;
        this.ceremonyDone = true;
        const fresh0 = client.hkTable.value;
        if (fresh0) { this.st = applyHaft(fresh0); this.gotState = true; }
        if (!this.seatsLaid) { this.scene.layoutSeats(this.st); this.seatsLaid = true; }
        // میزِ نپخش — [[CbOnline.beforeDeal]]: وضعیتِ سرور دستِ پخش‌شده را دارد،
        // ولی مراسم پیش از پخش است. پخش کارِ رویدادِ deal است.
        this.scene.render(beforeDeal(this.st));
        const from = toLocalSafe(ev.fromSeat, this.st.me, this.st.n);
        // مراسم درنگِ خودخواسته است، نه عقب‌ماندگی — [[Sequencer.linger]]. پخش از پیش پشتش در
        // صف است؛ بی این، بعدِ ده ثانیه آس‌کشی «کهنه» دیده می‌شد و پخش و «شروع!» تا چهاربرابر
        // تند می‌آمدند (آزمونِ prod، ۲۸ اکتبر ۲۰۲۶: ۱٫۹ ثانیه به‌جای ~۷ ثانیه).
        await this.seq.linger((async () => {
          await this.scene.animStarterDraw(ev.draw, from, toLocalSafe(ev.seat, this.st.me, this.st.n), this.name(toLocalSafe(ev.seat, this.st.me, this.st.n)));
          await this.scene.animShuffle();
        })());
        this.scene.render(beforeDeal(this.st));
        client.hkAce.value = null;
        break;
      }

      case "deal": {
        // دستِ تازه با همین رویداد شروع می‌شود، پیش از وضعیتش: پنلِ نتیجه همین‌جا می‌ماند تا دیده شود،
        // و بعد همین‌جا بسته می‌شود — وگرنه پخشِ دستِ تازه (در کندترین سرعت ~۸ ثانیه) زیرِ پنل می‌گذشت.
        await this.brk.keep(this.seq, undefined, !!this.overlay.value && !this.st.matchOver);
        if (this.quitting) return;
        if (!!this.overlay.value && !this.st.matchOver) this.overlay.value = false;
        // دستِ تازه باید **پخش** شود، نه این‌که یک‌باره روی صفحه باشد.
        //
        // سرور این رویداد را پیش از اولین وضعیت می‌فرستد و ۱٫۵ ثانیه هم مکث
        // می‌گذارد تا همین‌جا خرج شود (haft.go → opt.Scene). کنترلرِ آنلاینِ اپ
        // این رویداد را نمی‌گیرد؛ این‌جا می‌گیریم — مثلِ مراسمِ آس‌کشیِ حکم.
        if (!this.gotState) {
          const first = client.hkTable.value;
          if (!first) break;            // هنوز چیزی نرسیده؛ رندرِ وضعیت خودش می‌آید
          this.st = applyHaft(first);
          this.raw.value = first;
          this.gotState = true;
        }
        if (!this.carpetApplied && this.st.carpet) { showCarpet(this.st.carpet); this.carpetApplied = true; }
        if (!this.seatsLaid) { this.scene.layoutSeats(this.st); this.seatsLaid = true; }
        await this.scene.animDeal(this.st, dealSize(ev, this.st));
        // یک بار، سرِ شروعِ مسابقه.
        if (!this.startShown) { this.startShown = true; await this.scene.animStartBanner("شروع!"); }
        client.hkDeal.value = null;
        break;
      }

      case "played":
        if (ev.card) await this.scene.animFlyToDiscard(this.st, seat, ev.card);
        break;
      case "drew":
      case "penalty":
        for (let i = 0; i < ev.count && i < 12; i++) await this.scene.animDrawOne(seat);
        break;
      case "suit":
        this.scene.flashRuling(ev.suit);
        break;
      // «۲» هدفش را انتخاب کرد. پیش از این هیچ نشانی نداشت و کارت بی‌مقدمه
      // جابه‌جا می‌شد؛ حالا اول معلوم می‌شود قربانی کیست.
      case "target":
        this.scene.flashTarget(victim);
        this.scene.toast(`${this.name(seat)} «${this.name(victim)}» را نشانه گرفت`);
        break;
      // تعارف انجام شد: یک برگ از دستِ seat به دستِ victim رفت.
      case "offer":
        if (this.tookOffer) { this.tookOffer = false; break; }
        await this.scene.animPass(seat, victim);
        break;
      case "bomb":
        this.scene.sevenEffect(0, ev.count, victim);
        break;
      case "warn":
        this.scene.showUnoFx(seat === 0 && !this.st.watching ? "" : (this.st.names[seat] ?? ""));
        break;
      case "forgot":
        // اپ این‌جا victim را می‌زند؛ ولی در «یادش رفت» خودِ seat جریمه شده است
        void this.scene.hammerFx(seat);
        break;
      case "caught":
        void this.scene.hammerFx(victim);
        break;
      case "seat_bot":
        this.scene.toast(`${this.name(seat)} رفت — ربات جایش نشست`);
        break;
      case "seat_reconnect":
        this.scene.toast(`${this.name(seat)} برگشت`);
        break;
    }
  }

  name(l: number): string { return l === 0 ? "تو" : botText(this.st.names[l] || "حریف", this.st.bots[l]); }
}

const toLocalSafe = (s: number, me: number, n: number) => (n <= 0 ? 0 : ((s - me) % n + n) % n);

/**
 * نمای میز پیش از پخشِ کارت — دست‌ها خالی، رویِ دسته خالی، نوبت هیچ‌کس.
 * دوقلوی [[CbOnline]]؛ خودِ st دست نمی‌خورد تا رویدادِ deal همان را پخش کند.
 */
function beforeDeal(st: HaftLocal): HaftLocal {
  return { ...st, myHand: [], playable: [], counts: st.counts.map(() => 0), top: null, turn: -1 };
}
